// select / multiselect — options come from the schema's OptionSource, so an
// inline list and a canonical reference domain render identically.
import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  InputAdornment,
  ListItemText,
  MenuItem,
  TextField,
} from "@mui/material";
import FieldFrame from "./FieldFrame";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import useReferenceOptions from "../../reference/useReferenceOptions";
import { invalidateReference, matchReferenceText } from "../../reference/referenceProvider";

const SelectInput = ({ field, value, values, error, disabled, pendingParentLabel, onChange, onBlur }) => {
  const id = `f-${field.name}`;
  const [retry, setRetry] = useState(0);
  const { options, loading, error: loadError } = useReferenceOptions(field.options, values, retry);
  const multiple = field.type === "multiselect";

  // Document extraction gives us free text — "UNITED ARAB EMIRATES", "U.A.E",
  // "Emirati" — where this field holds a canonical code. Once the options have
  // loaded, resolve that text to a code so the OCR pre-fill actually shows as a
  // selection instead of silently rendering an empty dropdown.
  //
  // Done here rather than in the OCR mapper because only this component knows
  // what the valid options are, and it makes every reference-backed select in
  // every vertical benefit without any per-field configuration.
  const reconciled = useRef(false);
  useEffect(() => {
    if (multiple || loading || !options.length) return;
    if (value === undefined || value === null || value === "") return;
    if (options.some((o) => o.value === value)) return; // already a valid code
    if (reconciled.current) return;

    const matched = matchReferenceText(
      options.map((o) => ({ code: o.value, label: o.label, aliases: o.aliases })),
      value
    );
    reconciled.current = true;
    // No confident match: clear it rather than leave an unsubmittable value
    // sitting in the field looking accepted.
    //
    // `normalising` tells the form this is the same answer in the carrier's
    // spelling, not a new choice, so fields that depend on this one keep the
    // text they are holding until their own list can resolve it.
    onChange({ target: { value: matched || "" }, normalising: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, options, value, multiple]);

  // A dependent list (model-after-make, breed-after-group) stays disabled until
  // every parent is set, which reads better than an empty dropdown the user can
  // open and scroll.
  const waitingOnParent = Boolean(pendingParentLabel);

  // Loaded successfully, but the carrier has nothing for this combination —
  // Orient genuinely lists no models for some makes, and answers
  // StatusCode 0 / "No vehicle models found". Left alone the control renders as
  // an ordinary enabled dropdown that opens onto nothing, which reads as
  // broken. Say what happened instead, and keep it disabled: there is nothing
  // to choose.
  const loadedEmpty = !loading && !loadError && !waitingOnParent && options.length === 0;

  const placeholder = loading
    ? "Loading…"
    : loadError
    ? "Could not load options"
    : waitingOnParent
    ? `Select ${pendingParentLabel} first`
    : loadedEmpty
    ? field.props?.emptyText || "None available"
    : field.placeholder || "Select";

  /** @type {string[]} */
  const selectedList = Array.isArray(value) ? value : [];
  const current = multiple ? selectedList : value ?? "";

  return (
    <FieldFrame
      label={field.label}
      required={field.required}
      help={loadedEmpty ? undefined : field.help}
      // When the carrier lists nothing, "Model is required" is true but
      // useless — the customer cannot satisfy it by trying harder. Replace it
      // with what they can actually do, so the field states the way out
      // instead of a rule they cannot meet.
      error={loadedEmpty ? field.props?.emptyHint || "No options available" : error}
      htmlFor={id}
    >
      {loadError && (
        <Box role="alert" sx={{ mb: 1 }}>
          <Button
            size="small"
            onClick={() => {
              if (!Array.isArray(field.options)) invalidateReference(field.options?.domain);
              setRetry((n) => n + 1);
            }}
            startIcon={<RefreshRoundedIcon sx={{ fontSize: 15 }} />}
            sx={{
              textTransform: "none",
              fontWeight: 600,
              fontSize: "0.8125rem",
              color: "#d4575a",
              p: 0,
              "&:hover": { bgcolor: "transparent", textDecoration: "underline" },
            }}
          >
            Couldn't load these options — try again
          </Button>
        </Box>
      )}
      <TextField
        id={id}
        select
        fullWidth
        size="small"
        variant="outlined"
        name={field.name}
        value={current}
        disabled={disabled || loading || waitingOnParent || loadedEmpty || !!loadError}
        error={!!error}
        onChange={onChange}
        onBlur={onBlur}
        SelectProps={{
          multiple,
          displayEmpty: true,
          renderValue: (selected) => {
            if (multiple) {
              const picked = /** @type {string[]} */ (selected);
              if (!picked?.length) return <Placeholder text={placeholder} />;
              return options
                .filter((o) => picked.includes(o.value))
                .map((o) => o.label)
                .join(", ");
            }
            if (selected === "" || selected === undefined) {
              return <Placeholder text={placeholder} />;
            }
            return options.find((o) => o.value === selected)?.label ?? selected;
          },
          MenuProps: { PaperProps: { sx: { maxHeight: 320 } } },
        }}
        InputProps={
          loading
            ? {
                endAdornment: (
                  <InputAdornment position="end" sx={{ mr: 2 }}>
                    <CircularProgress size={16} sx={{ color: "#C9A777" }} />
                  </InputAdornment>
                ),
              }
            : undefined
        }
        {...field.props}
      >
        {options.map((option) => (
          <MenuItem key={option.value} value={option.value}>
            {multiple && (
              <Checkbox
                size="small"
                checked={selectedList.includes(option.value)}
                sx={{ p: 0.5, mr: 1, "&.Mui-checked": { color: "#C9A777" } }}
              />
            )}
            <ListItemText primary={option.label} secondary={option.hint} />
          </MenuItem>
        ))}
      </TextField>
    </FieldFrame>
  );
};

const Placeholder = ({ text }) => (
  <span style={{ color: "rgba(123,110,102,0.65)" }}>{text}</span>
);

export default SelectInput;
